import React, { useState, useRef } from 'react';
import { 
  ArrowLeft,
  User,
  Camera,
  Edit3,
  Save,
  X,
  Check,
  Eye,
  EyeOff,
  Upload,
  FileText,
  Shield,
  CreditCard,
  Building2,
  MapPin,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  Briefcase,
  Home,
  Users,
  Award,
  AlertCircle,
  CheckCircle2,
  Info,
  Sun,
  Moon,
  Star,
  Zap,
  Lock,
  Image,
  Download,
  Trash2,
  RefreshCw,
  ExternalLink,
  Plus,
  Minus,
  Globe,
  Flag
} from 'lucide-react';

const UserProfile = ({ theme, isDarkMode })  => {

  const [currentTab, setCurrentTab] = useState('personal');
  const [isEditing, setIsEditing] = useState({});
  const [showSensitiveData, setShowSensitiveData] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  const [kycStatus, setKycStatus] = useState('verified'); // pending, verified, rejected
  const fileInputRef = useRef(null);


  const [personalInfo, setPersonalInfo] = useState({
    firstName: 'Alex',
    lastName: 'Johnson',
    middleName: 'Michael',
    email: 'alex.johnson@email.com',
    phone: '+1 (555) 123-4567',
    alternatePhone: '+1 (555) 987-6543',
    dateOfBirth: '1990-05-15',
    gender: 'Male',
    maritalStatus: 'Single',
    nationality: 'American',
    preferredLanguage: 'English'
  });

  const [addressInfo, setAddressInfo] = useState({
    street: '123 Main Street',
    apartment: 'Apt 4B',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    country: 'United States',
    residenceType: 'Rent',
    residenceDuration: '3 years',
    previousAddress: '456 Oak Avenue, Brooklyn, NY 11201'
  });

  const [employmentInfo, setEmploymentInfo] = useState({
    occupation: 'Software Engineer',
    employer: 'Tech Solutions Inc.',
    workAddress: '789 Business Blvd, New York, NY 10005',
    workPhone: '+1 (555) 456-7890',
    employmentType: 'Full-time',
    employmentDuration: '2.5 years',
    department: 'Engineering',
    position: 'Senior Software Engineer',
    previousEmployer: 'StartUp Co.'
  });

  const [financialInfo, setFinancialInfo] = useState({
    annualIncome: '85000',
    monthlyIncome: '7083',
    otherIncome: '5000',
    incomeSource: 'Employment',
    bankName: 'Chase Bank',
    accountType: 'Checking',
    accountNumber: '****2847',
    routingNumber: '021000021',
    creditScore: '745',
    existingDebts: '12500',
    monthlyExpenses: '4200'
  });

  const [kycDocuments, setKycDocuments] = useState({
    idDocument: {
      type: 'Driver License',
      number: 'DL123456789',
      expiryDate: '2027-05-15',
      issuingState: 'NY',
      status: 'verified',
      uploadDate: '2024-01-15'
    },
    proofOfAddress: {
      type: 'Utility Bill',
      status: 'verified',
      uploadDate: '2024-01-15'
    },
    proofOfIncome: {
      type: 'Pay Stub',
      status: 'verified',
      uploadDate: '2024-01-20'
    },
    socialSecurity: {
      number: '***-**-4567',
      status: 'verified',
      verificationDate: '2024-01-25'
    }
  });

  const [emergencyContacts, setEmergencyContacts] = useState([
    {
      id: 1,
      name: 'Sarah Johnson',
      relationship: 'Sister',
      phone: '+1 (555) 234-5678',
      email: 'sarah.johnson@email.com',
      isPrimary: true
    },
    {
      id: 2,
      name: 'Michael Johnson',
      relationship: 'Father',
      phone: '+1 (555) 345-6789',
      email: 'michael.johnson@email.com',
      isPrimary: false
    }
  ]);

  const tabs = [
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'address', label: 'Address', icon: MapPin },
    { id: 'employment', label: 'Employment', icon: Briefcase },
    { id: 'financial', label: 'Financial', icon: DollarSign },
    { id: 'kyc', label: 'KYC', icon: Shield },
    { id: 'contacts', label: 'Emergency', icon: Users }
  ];

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleEdit = (field) => {
    setIsEditing(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const toggleSensitiveData = (field) => {
    setShowSensitiveData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const updateField = (section, field, value) => {
    switch(section) {
      case 'personal':
        setPersonalInfo(prev => ({ ...prev, [field]: value }));
        break;
      case 'address':
        setAddressInfo(prev => ({ ...prev, [field]: value }));
        break;
      case 'employment':
        setEmploymentInfo(prev => ({ ...prev, [field]: value }));
        break;
      case 'financial':
        setFinancialInfo(prev => ({ ...prev, [field]: value }));
        break;
    }
  };

  const EditableField = ({ 
    label, 
    value, 
    field, 
    section, 
    type = 'text', 
    sensitive = false, 
    options = null,
    placeholder = '',
    icon: Icon 
  }) => {
    const isEditingField = isEditing[`${section}-${field}`];
    const shouldShowSensitive = showSensitiveData[`${section}-${field}`] || !sensitive;

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className={`text-sm font-medium ${theme.text}`}>{label}</label>
          <button
            onClick={() => toggleEdit(`${section}-${field}`)}
            className={`text-xs ${theme.accent} font-medium`}
          >
            {isEditingField ? 'Cancel' : 'Edit'}
          </button>
        </div>
        
        {isEditingField ? (
          <div className="space-y-2">
            {options ? (
              <select
                value={value}
                onChange={(e) => updateField(section, field, e.target.value)}
                className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
              >
                {options.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : (
              <div className="relative">
                {Icon && (
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <Icon className={`w-4 h-4 ${theme.textMuted}`} />
                  </div>
                )}
                <input
                  type={type}
                  value={value}
                  onChange={(e) => updateField(section, field, e.target.value)}
                  placeholder={placeholder}
                  className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
                />
              </div>
            )}
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  toggleEdit(`${section}-${field}`);
                  // Here you would typically save to backend
                }}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-500 text-white rounded-md text-xs font-medium"
              >
                <Save className="w-3 h-3" />
                <span>Save</span>
              </button>
              <button
                onClick={() => toggleEdit(`${section}-${field}`)}
                className={`flex items-center space-x-1 px-3 py-1 ${theme.cardBg} border ${theme.border} rounded-md text-xs font-medium ${theme.text}`}
              >
                <X className="w-3 h-3" />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            {Icon && <Icon className={`w-4 h-4 ${theme.textMuted}`} />}
            <span className={`flex-1 ${theme.textSecondary}`}>
              {sensitive && !shouldShowSensitive ? '••••••••' : value || 'Not provided'}
            </span>
            {sensitive && (
              <button
                onClick={() => toggleSensitiveData(`${section}-${field}`)}
                className={`text-xs ${theme.textMuted}`}
              >
                {shouldShowSensitive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const StatusBadge = ({ status, size = 'sm' }) => {
    const colors = {
      verified: 'bg-emerald-500/20 text-emerald-500',
      pending: 'bg-amber-500/20 text-amber-500',
      rejected: 'bg-red-500/20 text-red-500',
      expired: 'bg-gray-500/20 text-gray-500'
    };

    const icons = {
      verified: CheckCircle2,
      pending: RefreshCw,
      rejected: X,
      expired: AlertCircle
    };

    const Icon = icons[status];
    const sizeClasses = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm';

    return (
      <div className={`inline-flex items-center space-x-1 ${colors[status]} rounded-full ${sizeClasses} font-medium`}>
        <Icon className={`w-3 h-3 ${size === 'lg' ? 'w-4 h-4' : ''}`} />
        <span className="capitalize">{status}</span>
      </div>
    );
  };

  const PersonalTab = () => (
    <div className="space-y-6">
      {/* Profile Picture */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              {profileImage ? (
                <img 
                  src={profileImage} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white text-2xl font-bold">
                  {personalInfo.firstName[0]}{personalInfo.lastName[0]}
                </span>
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            >
              <Camera className="w-4 h-4 text-white" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
          
          <div className="flex-1">
            <h3 className={`text-lg font-semibold ${theme.text}`}>
              {personalInfo.firstName} {personalInfo.lastName}
            </h3>
            <p className={`text-sm ${theme.textMuted}`}>{personalInfo.email}</p>
            <div className="flex items-center space-x-2 mt-2">
              <StatusBadge status="verified" />
              <span className={`text-xs ${theme.textMuted}`}>Profile verified</span>
            </div>
          </div>
          
          <button className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors">
            <Upload className="w-4 h-4" />
            <span>Upload Photo</span>
          </button>
        </div>
      </div>

      {/* Basic Information */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Basic Information</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EditableField
            label="First Name"
            value={personalInfo.firstName}
            field="firstName"
            section="personal"
            icon={User}
          />
          
          <EditableField
            label="Last Name"
            value={personalInfo.lastName}
            field="lastName"
            section="personal"
            icon={User}
          />
          
          <EditableField
            label="Middle Name"
            value={personalInfo.middleName}
            field="middleName"
            section="personal"
            icon={User}
            placeholder="Optional"
          />
          
          <EditableField
            label="Date of Birth"
            value={personalInfo.dateOfBirth}
            field="dateOfBirth"
            section="personal"
            type="date"
            icon={Calendar}
          />
          
          <EditableField
            label="Gender"
            value={personalInfo.gender}
            field="gender"
            section="personal"
            options={['Male', 'Female', 'Other', 'Prefer not to say']}
          />
          
          <EditableField
            label="Marital Status"
            value={personalInfo.maritalStatus}
            field="maritalStatus"
            section="personal"
            options={['Single', 'Married', 'Divorced', 'Widowed', 'Separated']}
          />
          
          <EditableField
            label="Nationality"
            value={personalInfo.nationality}
            field="nationality"
            section="personal"
            icon={Flag}
          />
          
          <EditableField
            label="Preferred Language"
            value={personalInfo.preferredLanguage}
            field="preferredLanguage"
            section="personal"
            options={['English', 'Spanish', 'French', 'German', 'Portuguese', 'Chinese']}
            icon={Globe}
          />
        </div>
      </div>

      {/* Contact Information */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Contact Information</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EditableField
            label="Email Address"
            value={personalInfo.email}
            field="email"
            section="personal"
            type="email"
            icon={Mail}
          />
          
          <EditableField
            label="Phone Number"
            value={personalInfo.phone}
            field="phone"
            section="personal"
            type="tel"
            icon={Phone}
          />
          
          <EditableField
            label="Alternate Phone"
            value={personalInfo.alternatePhone}
            field="alternatePhone"
            section="personal"
            type="tel"
            icon={Phone}
            placeholder="Optional"
          />
        </div>
      </div>
    </div>
  );

  const AddressTab = () => (
    <div className="space-y-6">
      {/* Current Address */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Current Address</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <EditableField
              label="Street Address"
              value={addressInfo.street}
              field="street"
              section="address"
              icon={MapPin}
            />
          </div>
          
          <EditableField
            label="Apartment/Unit"
            value={addressInfo.apartment}
            field="apartment"
            section="address"
            placeholder="Optional"
            icon={Home}
          />
          
          <EditableField
            label="City"
            value={addressInfo.city}
            field="city"
            section="address"
            icon={Building2}
          />
          
          <EditableField
            label="State/Province"
            value={addressInfo.state}
            field="state"
            section="address"
          />
          
          <EditableField
            label="ZIP/Postal Code"
            value={addressInfo.zipCode}
            field="zipCode"
            section="address"
          />
          
          <EditableField
            label="Country"
            value={addressInfo.country}
            field="country"
            section="address"
            icon={Flag}
          />
          
          <EditableField
            label="Residence Type"
            value={addressInfo.residenceType}
            field="residenceType"
            section="address"
            options={['Own', 'Rent', 'Family', 'Other']}
          />
        </div>
      </div>

      {/* Previous Address */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Previous Address</h4>
        <p className={`text-sm ${theme.textMuted} mb-4`}>
          Required if you've lived at current address for less than 2 years
        </p>
        
        <EditableField
          label="Previous Address"
          value={addressInfo.previousAddress}
          field="previousAddress"
          section="address"
          placeholder="Complete previous address"
          icon={MapPin}
        />
        
        <div className="mt-4">
          <EditableField
            label="Duration at Current Address"
            value={addressInfo.residenceDuration}
            field="residenceDuration"
            section="address"
            placeholder="e.g., 2 years 3 months"
            icon={Calendar}
          />
        </div>
      </div>
    </div>
  );

  const EmploymentTab = () => (
    <div className="space-y-6">
      {/* Current Employment */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Current Employment</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EditableField
            label="Job Title/Occupation"
            value={employmentInfo.occupation}
            field="occupation"
            section="employment"
            icon={Briefcase}
          />
          
          <EditableField
            label="Employer Name"
            value={employmentInfo.employer}
            field="employer"
            section="employment"
            icon={Building2}
          />
          
          <EditableField
            label="Employment Type"
            value={employmentInfo.employmentType}
            field="employmentType"
            section="employment"
            options={['Full-time', 'Part-time', 'Contract', 'Self-employed', 'Unemployed', 'Retired', 'Student']}
          />
          
          <EditableField
            label="Department"
            value={employmentInfo.department}
            field="department"
            section="employment"
            placeholder="Optional"
          />
          
          <EditableField
            label="Position Level"
            value={employmentInfo.position}
            field="position"
            section="employment"
            options={['Entry Level', 'Associate', 'Senior', 'Lead', 'Manager', 'Director', 'Executive']}
          />
          
          <EditableField
            label="Employment Duration"
            value={employmentInfo.employmentDuration}
            field="employmentDuration"
            section="employment"
            placeholder="e.g., 2 years 6 months"
            icon={Calendar}
          />
          
          <div className="md:col-span-2">
            <EditableField
              label="Work Address"
              value={employmentInfo.workAddress}
              field="workAddress"
              section="employment"
              placeholder="Complete work address"
              icon={MapPin}
            />
          </div>
          
          <EditableField
            label="Work Phone"
            value={employmentInfo.workPhone}
            field="workPhone"
            section="employment"
            type="tel"
            icon={Phone}
          />
        </div>
      </div>

      {/* Previous Employment */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Previous Employment</h4>
        <p className={`text-sm ${theme.textMuted} mb-4`}>
          Required if current employment is less than 2 years
        </p>
        
        <EditableField
          label="Previous Employer"
          value={employmentInfo.previousEmployer}
          field="previousEmployer"
          section="employment"
          placeholder="Previous company name"
          icon={Building2}
        />
      </div>
    </div>
  );

  const FinancialTab = () => (
    <div className="space-y-6">
      {/* Income Information */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Income Information</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EditableField
            label="Annual Income"
            value={financialInfo.annualIncome}
            field="annualIncome"
            section="financial"
            type="number"
            sensitive={true}
            icon={DollarSign}
          />
          
          <EditableField
            label="Monthly Income"
            value={financialInfo.monthlyIncome}
            field="monthlyIncome"
            section="financial"
            type="number"
            sensitive={true}
            icon={DollarSign}
          />
          
          <EditableField
            label="Other Income"
            value={financialInfo.otherIncome}
            field="otherIncome"
            section="financial"
            type="number"
            sensitive={true}
            placeholder="Investments, side business, etc."
            icon={DollarSign}
          />
          
          <EditableField
            label="Primary Income Source"
            value={financialInfo.incomeSource}
            field="incomeSource"
            section="financial"
            options={['Employment', 'Self-employed', 'Investment', 'Pension', 'Benefits', 'Other']}
          />
        </div>
      </div>

      {/* Banking Information */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <div className="flex items-center space-x-2 mb-4">
          <Lock className="w-5 h-5 text-emerald-500" />
          <h4 className={`text-lg font-semibold ${theme.text}`}>Banking Information</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EditableField
            label="Bank Name"
            value={financialInfo.bankName}
            field="bankName"
            section="financial"
            icon={Building2}
          />
          
          <EditableField
            label="Account Type"
            value={financialInfo.accountType}
            field="accountType"
            section="financial"
            options={['Checking', 'Savings', 'Money Market', 'Other']}
          />
          
          <EditableField
            label="Account Number"
            value={financialInfo.accountNumber}
            field="accountNumber"
            section="financial"
            sensitive={true}
            placeholder="Last 4 digits shown"
            icon={CreditCard}
          />
          
          <EditableField
            label="Routing Number"
            value={financialInfo.routingNumber}
            field="routingNumber"
            section="financial"
            sensitive={true}
            icon={CreditCard}
          />
        </div>
      </div>

      {/* Financial Overview */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Financial Overview</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <EditableField
            label="Credit Score"
            value={financialInfo.creditScore}
            field="creditScore"
            section="financial"
            type="number"
            icon={Award}
          />
          
          <EditableField
            label="Existing Debts"
            value={financialInfo.existingDebts}
            field="existingDebts"
            section="financial"
            type="number"
            sensitive={true}
            icon={DollarSign}
          />
          
          <EditableField
            label="Monthly Expenses"
            value={financialInfo.monthlyExpenses}
            field="monthlyExpenses"
            section="financial"
            type="number"
            sensitive={true}
            icon={DollarSign}
          />
        </div>
      </div>
    </div>
  );

  const KYCTab = () => (
    <div className="space-y-6">
      {/* KYC Status Overview */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <div className="flex items-center justify-between mb-4">
          <h4 className={`text-lg font-semibold ${theme.text}`}>KYC Verification Status</h4>
          <StatusBadge status={kycStatus} size="lg" />
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className={`text-2xl font-bold ${theme.success}`}>4/4</div>
            <div className={`text-xs ${theme.textMuted}`}>Documents Verified</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${theme.accent}`}>100%</div>
            <div className={`text-xs ${theme.textMuted}`}>Completion Rate</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${theme.text}`}>AA</div>
            <div className={`text-xs ${theme.textMuted}`}>Risk Rating</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${theme.text}`}>Jan 25</div>
            <div className={`text-xs ${theme.textMuted}`}>Last Updated</div>
          </div>
        </div>
      </div>

      {/* Identity Documents */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Identity Documents</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-emerald-500/30 bg-emerald-500/10 rounded-lg">
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-emerald-500" />
              <div>
                <div className={`font-medium ${theme.text}`}>Driver License</div>
                <div className={`text-sm ${theme.textMuted}`}>DL123456789 • Expires May 2027</div>
              </div>
            </div>
            <StatusBadge status="verified" />
          </div>

          <div className="flex items-center justify-between p-4 border border-emerald-500/30 bg-emerald-500/10 rounded-lg">
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-emerald-500" />
              <div>
                <div className={`font-medium ${theme.text}`}>Social Security</div>
                <div className={`text-sm ${theme.textMuted}`}>***-**-4567 • Verified</div>
              </div>
            </div>
            <StatusBadge status="verified" />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <button className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors">
            <Upload className="w-4 h-4" />
            <span>Upload New Document</span>
          </button>
          
          <button className="flex items-center justify-center space-x-2 px-4 py-3 border border-blue-500 text-blue-500 rounded-lg font-medium hover:bg-blue-500/10 transition-colors">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh Verification</span>
          </button>
        </div>
      </div>

      {/* Additional Verification */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Additional Verification</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-emerald-500/30 bg-emerald-500/10 rounded-lg">
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-emerald-500" />
              <div>
                <div className={`font-medium ${theme.text}`}>Proof of Address</div>
                <div className={`text-sm ${theme.textMuted}`}>Utility Bill • Verified</div>
              </div>
            </div>
            <StatusBadge status="verified" />
          </div>

          <div className="flex items-center justify-between p-4 border border-emerald-500/30 bg-emerald-500/10 rounded-lg">
            <div className="flex items-center space-x-3">
              <FileText className="w-5 h-5 text-emerald-500" />
              <div>
                <div className={`font-medium ${theme.text}`}>Proof of Income</div>
                <div className={`text-sm ${theme.textMuted}`}>Pay Stub • Verified</div>
              </div>
            </div>
            <StatusBadge status="verified" />
          </div>
        </div>
      </div>

      {/* Verification History */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Verification History</h4>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <div>
                <div className={`text-sm ${theme.text}`}>Identity Verification</div>
                <div className={`text-xs ${theme.textMuted}`}>Completed on Jan 25, 2024</div>
              </div>
            </div>
            <StatusBadge status="verified" />
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <div>
                <div className={`text-sm ${theme.text}`}>Address Verification</div>
                <div className={`text-xs ${theme.textMuted}`}>Completed on Jan 20, 2024</div>
              </div>
            </div>
            <StatusBadge status="verified" />
          </div>

          <div className="flex items-center justify-between py-3">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <div>
                <div className={`text-sm ${theme.text}`}>Income Verification</div>
                <div className={`text-xs ${theme.textMuted}`}>Completed on Jan 18, 2024</div>
              </div>
            </div>
            <StatusBadge status="verified" />
          </div>
        </div>
      </div>
    </div>
  );

  const EmergencyContactsTab = () => (
    <div className="space-y-6">
      {/* Primary Emergency Contact */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Emergency Contacts</h4>
        
        {emergencyContacts.map((contact) => (
          <div key={contact.id} className="p-4 border border-blue-500/30 bg-blue-500/10 rounded-lg mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-blue-500" />
                <div>
                  <div className={`font-medium ${theme.text}`}>{contact.name}</div>
                  <div className={`text-sm ${theme.textMuted}`}>{contact.relationship}</div>
                </div>
              </div>
              {contact.isPrimary && (
                <div className="px-2 py-1 bg-blue-500/20 text-blue-500 text-xs rounded-full font-medium">
                  Primary
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-gray-500" />
                <span className={`text-sm ${theme.text}`}>{contact.phone}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-gray-500" />
                <span className={`text-sm ${theme.text}`}>{contact.email}</span>
              </div>
            </div>
            
            <div className="flex space-x-2 mt-4">
              <button className="flex items-center space-x-1 px-3 py-1 bg-blue-500 text-white rounded-md text-xs font-medium">
                <Edit3 className="w-3 h-3" />
                <span>Edit</span>
              </button>
              <button className="flex items-center space-x-1 px-3 py-1 border border-red-500 text-red-500 rounded-md text-xs font-medium">
                <Trash2 className="w-3 h-3" />
                <span>Remove</span>
              </button>
            </div>
          </div>
        ))}

        <button className="flex items-center space-x-2 px-4 py-3 border-2 border-dashed border-blue-500/50 text-blue-500 rounded-lg font-medium hover:bg-blue-500/10 transition-colors w-full justify-center">
          <Plus className="w-5 h-5" />
          <span>Add Emergency Contact</span>
        </button>
      </div>

      {/* Emergency Contact Guidelines */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-6 border ${theme.border}`}>
        <h4 className={`text-lg font-semibold ${theme.text} mb-4`}>Guidelines</h4>
        
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <Info className="w-4 h-4 text-blue-500 mt-0.5" />
            <div>
              <div className={`text-sm font-medium ${theme.text}`}>Primary Contact</div>
              <div className={`text-xs ${theme.textMuted}`}>Your primary contact will be notified first in case of emergencies.</div>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <Info className="w-4 h-4 text-blue-500 mt-0.5" />
            <div>
              <div className={`text-sm font-medium ${theme.text}`}>Contact Requirements</div>
              <div className={`text-xs ${theme.textMuted}`}>Contacts should be family members or close friends who can be reached easily.</div>
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <Info className="w-4 h-4 text-blue-500 mt-0.5" />
            <div>
              <div className={`text-sm font-medium ${theme.text}`}>Updates</div>
              <div className={`text-xs ${theme.textMuted}`}>Please keep your emergency contacts updated with current information.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentTab = () => {
    switch (currentTab) {
      case 'personal':
        return <PersonalTab />;
      case 'address':
        return <AddressTab />;
      case 'employment':
        return <EmploymentTab />;
      case 'financial':
        return <FinancialTab />;
      case 'kyc':
        return <KYCTab />;
      case 'contacts':
        return <EmergencyContactsTab />;
      default:
        return <PersonalTab />;
    }
  };

  return (
    <div className={`min-h-screen ${theme.bg} transition-all duration-300`}>
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/10 dark:bg-black/10 border-b border-white/10 dark:border-black/20">
        <div className="flex items-center justify-between p-4 max-w-7xl mx-auto">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => window.history.back()}
              className="p-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </button>
            <h1 className={`text-xl font-bold ${theme.text}`}>Profile Settings</h1>
          </div>          
         
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-20 pb-20 px-4 max-w-7xl mx-auto">
        {/* Tab Navigation */}
        <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-2 border ${theme.border} mb-6 sticky top-20 z-40`}>
          <div className="flex overflow-x-auto space-x-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setCurrentTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    currentTab === tab.id
                      ? 'bg-blue-500 text-white shadow-lg'
                      : `${theme.text} hover:bg-white/5`
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        {renderCurrentTab()}
      </div>

      {/* Save Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/10 dark:bg-black/10 border-t border-white/10 dark:border-black/20">
        <div className="flex items-center justify-between p-4 max-w-7xl mx-auto">
          <div className={`text-sm ${theme.textMuted}`}>
            Last updated: {new Date().toLocaleDateString()}
          </div>
          
          <div className="flex space-x-3">
            <button className={`px-4 py-2 border ${theme.border} rounded-lg font-medium ${theme.text} hover:bg-white/5 transition-colors`}>
              Cancel
            </button>
            <button className="px-6 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors">
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;